import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { HEARD_OPTIONS } from "@/lib/heardOptions";
import { Button } from "@/components/ui/button";
import { CircleNotch } from "@phosphor-icons/react";

// One-time question shown to logged-in users (mainly Google signups) who haven't told us
// how they found Controlly yet. Blocks the app behind a small overlay until answered.
export default function OnboardingModal() {
  const { user, loading, checkAuth } = useAuth();
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);

  if (loading || !user || !user.needs_onboarding) return null;

  const submit = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      await api.post("/auth/onboarding", { heard_about_us: choice });
      await checkAuth();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" data-testid="onboarding-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-7 animate-in fade-in zoom-in-95 duration-300">
        <img src="/controlly-icon.png" alt="Controlly" className="w-10 h-10 rounded-md mb-5" />
        <h2 className="font-heading text-2xl font-bold tracking-tight">One quick question 👋</h2>
        <p className="text-sm text-zinc-400 mt-1.5">How did you hear about Controlly? It helps us a ton.</p>

        <div className="mt-5 space-y-2" data-testid="onboarding-options">
          {HEARD_OPTIONS.map((opt) => (
            <button
              key={opt}
              data-testid={`onboarding-option-${opt.slice(0, 6).toLowerCase().replace(/\W/g, "")}`}
              onClick={() => setChoice(opt)}
              className={`w-full text-left text-sm rounded-lg border px-4 py-2.5 transition-colors ${choice === opt ? "border-blue-500 bg-blue-500/10 text-white" : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"}`}
            >
              {opt}
            </button>
          ))}
        </div>

        <Button
          data-testid="onboarding-submit"
          onClick={submit}
          disabled={!choice || saving}
          className="w-full mt-6 h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md disabled:opacity-40"
        >
          {saving ? <CircleNotch size={18} className="animate-spin" /> : "Continue"}
        </Button>
      </div>
    </div>
  );
}
